import { Component, DestroyRef, EventEmitter, inject, Input, NgZone, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import { Display } from 'src/app/api/services/display';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { DisplayService } from 'src/app/displays/services/display.service';
import { FolderService } from 'src/app/shared/services/folder.service';
import { FeaturesService } from '../../plans/features.service';
import { ListOperations } from '../../scrolling-list';
import { PageWithListComponent } from '../../scrolling-list/page-with-list.component';
import { ListSearchStateService } from '../../scrolling-list/services/list-search-state.service';
import { PaginatedScrollingListService } from '../../scrolling-list/services/paginated-scrolling-list.service';
import { ScrollingListStateService } from '../../scrolling-list/services/scrolling-list-state.service';

@Component({
    selector: 'distribution-list',
    templateUrl: './distribution-list.component.html',
    styleUrls: ['./distribution-list.component.scss'],
    standalone: false,
    providers: [
      displayListProviders()
    ]
})
export class DistributionListComponent extends PageWithListComponent<Display> implements OnInit {
  /**
   * @belongsTo PageWithListComponent
   */
  listOperations: ListOperations<Display>;

  @Input() distribution: string[];
  @Input() subcompanyDistribution: string[];
  @Input() distributeToSubcompanies: boolean;
  @Input() enableSubcompanies: boolean = false;
  @Input() enableFeatureCheck: boolean = true;
  @Output() distributionChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() subcompanyDistributionChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() distributeToSubcompaniesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeEvent: EventEmitter<null> = new EventEmitter<null>();

  private destroyRef = inject(DestroyRef);

  scheduleOverrides: string[] = [];

  filterConfig = {
    placeholder: 'Search Displays',
    id: 'displaySearchInput'
  };

  protected list = this.listService.list;

  constructor(
    public displayService: DisplayService,
    public companyStateService: CompanyStateService,
    private featuresService: FeaturesService,
    private ngZone: NgZone,
    protected folderService: FolderService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.companyStateService.isRootCompanySelected()) {
      this.distributeToSubcompanies = false;
    }
    this.searchState.init({
      ...this.searchState.getSearchObject(),
      sortBy: 'name',
      reverse: false,
      includeSubcompanies: !this.subcompanyDistributionDisabled,
    });

    this.listService.load();
    this.onScroll.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(500)
    ).subscribe(() => {
      this.refresh();
    });
  }

  get subcompanyDistributionAvailable(): boolean | null {
    return !this.enableFeatureCheck || this.featuresService.isFeatureAvailable(FeaturesService.FEATURE_SUBCOMPANY_DISTRIBUTION);
  }

  get subcompanyDistributionDisabled() {
    return !this.subcompanyDistributionAvailable && !this.distributeToSubcompanies || this.companyStateService.isRootCompanySelected();
  }

  showUpgradePlanModal(): void {
    this.featuresService.showUpgradePlanModal(FeaturesService.FEATURE_SUBCOMPANY_DISTRIBUTION).then((result) => {
      if (result) {
        this.closeEvent.emit();
      }
    });
  }

  toggleAllCompanies (value) {
    if (!this.subcompanyDistributionDisabled) {
      this.distributeToSubcompanies = value;
      this.distributeToSubcompaniesChange.emit(value);

      this.searchState.setIncludeSubcompanies(value);
      this.searchState.doSearch();
    }
  }

  refresh () {
    setTimeout(() => {
      this.ngZone.run(() => {});
    });
  }

  showPopover (overrideNames: string[]) {
    this.scheduleOverrides = overrideNames;
    this.refresh();
  }

  private _updateDistribution (displayId, distribution, emitter) {
    const index = distribution.indexOf(displayId);
    if (index > -1) {
      distribution.splice(index, 1);
    } else {
      distribution.push(displayId);
    }
    emitter.emit(distribution);
  }

  toggleDisplay (displayId, companyId) {
    if (companyId === this.companyStateService.getSelectedCompanyId()) {
      this._updateDistribution(displayId, this.distribution, this.distributionChange);
    } else {
      this._updateDistribution(displayId, this.subcompanyDistribution, this.subcompanyDistributionChange);
    }
  }

  isSelected (displayId) {
    return this.distribution.indexOf(displayId) >= 0 || this.subcompanyDistribution.indexOf(displayId) >= 0;
  }

  areAllDisplaysSelected() {
    if (this.list().length === 0) {
      return false;
    }
    return this.list().every(display => this.isSelected(display.item.id));
  }

  toggleAllDisplays() {
    const allSelected = this.areAllDisplaysSelected();
    this.list().forEach(display => {
      if (allSelected) {
        // Remove from distribution
        const index = this.distribution.indexOf(display.item.id);
        if (index > -1) {
          this.distribution.splice(index, 1);
        }
        const subIndex = this.subcompanyDistribution.indexOf(display.item.id);
        if (subIndex > -1) {
          this.subcompanyDistribution.splice(subIndex, 1);
        }
      } else {
        // Add to distribution
        if (display.item.companyId === this.companyStateService.getSelectedCompanyId()) {
          if (this.distribution.indexOf(display.item.id) === -1) {
            this.distribution.push(display.item.id);
          }
        } else {
          if (this.subcompanyDistribution.indexOf(display.item.id) === -1) {
            this.subcompanyDistribution.push(display.item.id);
          }
        }
      }
    });
    this.distributionChange.emit(this.distribution);
    this.subcompanyDistributionChange.emit(this.subcompanyDistribution);
  }

  onFolderClicked(folderId: string) {
    this.searchState.setFolderId(folderId);
    this.searchState.doSearch();
  }

  getDestroyRef(): DestroyRef {
    return this.destroyRef;
  }

  protected doSearch = () => {
    this.searchState.doSearch();
  };
}

function displayListProviders() {
  return [
    { provide: 'WithListService', useExisting: DisplayApiService },
    { provide: 'ListKind', useValue: 'Display' },
    { provide: ListSearchStateService },
    { provide: ScrollingListStateService },
    { provide: PaginatedScrollingListService },
  ];
}
